import React, { useState, useEffect, useRef, useMemo } from "react";
import { useStaticQuery, graphql } from "gatsby"
import BlogTile from "./BlogTile";
import { DropDown } from "../UIElements/Form";
import { UserIcon } from '@heroicons/react/20/solid'
import Icon from "../UIElements/Icons";
import SearchButton from "../Header/SearchButton";
import SearchModal from "../SearchModal/SearchModal";

const isBrowser = typeof window !== "undefined"

const getWidth = () => {
  if (isBrowser) {
    return window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;
  }
  return null;
}

function useIsInViewport(ref) {
    const [isIntersecting, setIsIntersecting] = useState(false);
  
    const observer = useMemo(
      () =>
        {
            if (isBrowser) {
                return new IntersectionObserver(([entry]) =>
                    setIsIntersecting(entry.isIntersecting),
                )
            }
            return null;
        },
      [],
    );
  
    useEffect(() => {
      observer.observe(ref.current);
  
      return () => {
        observer.disconnect();
      };
    }, [ref, observer]);
  
    return isIntersecting;
  }

function BlogBlock({title, filterValues, filterChange}) {

    const data = useStaticQuery(graphql`
        query BlogQuery {
            allStrapiTilukBlog(sort: {publishedAt: DESC}) {
                edges {
                node {
                    id
                    createdBy {
                        id
                        lastname
                        firstname
                    }
                    Authors {
                        createdById
                        Name
                    }
                    Excerpt
                    Title
                    Slug
                    publishedAt(formatString: "D MMMM YYYY")
                    FeatureImageUrl
                    FeatureImage {
                        formats {
                            medium {
                                url
                            }
                        }
                        url
                    }
                    PostType
                    Imported
                    Content {
                    ... on STRAPI__COMPONENT_TILWEBSITE_BLOG_CONTENT {
                        id
                        Body {
                        data {
                            Body
                        }
                        }
                    }
                    }
                }
                }
            }
            allStrapiEmployee(filter: {blogAuthor: {eq: true}, Country: {eq: "UK"}}, sort: {Name: ASC}) {
                edges {
                node {
                    Name
                    createdById
                }
                }
            }
            postTypes: allStrapiTilukBlog {
                    distinct(field: {PostType: SELECT})
                }
            }
         `)

    const [page, setPage] = useState(0);
    const [perPage, setPerPage ] = useState(3);
    const [width, setWidth] = useState(getWidth());
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [authorOptions, setAuthorOptions] = useState([{
        name: 'All Authors',
        id: 0
    }]);
    const [postOptions, setPostOptions] = useState([{
        name: 'All Post Types',
        id: 0
    }]);
    const [isClient, setIsClient] = useState(false)
    const [searchOpen, setSearchOpen] = useState(false);

    const baseRef = useRef(null);

    const scrollToBase = useIsInViewport(baseRef);

    useEffect(() => {
        setIsClient(true)
		// timeoutId for debounce mechanism
		let timeoutId = null;
		const resizeListener = () => {
		  // prevent execution of previous setTimeout
		  clearTimeout(timeoutId);
		  // change width from the state object after 150 milliseconds
		  timeoutId = setTimeout(() => setWidth(getWidth()), 150);
		};
		// set resize listener
		window?.addEventListener("resize", resizeListener);
	
		// clean up function
		return () => {
		  // remove resize listener
		  window?.removeEventListener("resize", resizeListener);
		};
	  // eslint-disable-next-line react-hooks/exhaustive-deps
	  }, []);

    useEffect(() => {
        if (width >= 1024) {
            setPerPage(12);
        } else if (width >= 768) {
            setPerPage(10);
        } else {
            setPerPage(6);
        }
    }, [width]);
    

    useEffect(() => {
        setPage(page => page/perPage)
    }, [data, perPage])

    useEffect(() => {
        if (scrollToBase) {
            setTimeout(() => setPage(page => page + 1), 500);
        }
    }, [scrollToBase])

    useEffect(() => {
        const allPosts = data?.allStrapiTilukBlog?.edges || [];
        const somePosts = allPosts.filter(post => {
            const { createdBy, PostType } = post.node;

            let filterAuthor = 0;
            let filterType = 0;

            if (filterValues.author && filterValues.author > 0) {
                filterAuthor = filterValues.author && filterValues.author === createdBy.id;
            }

            if (filterValues.postType && filterValues.postType !== 0) {
                filterType = filterValues.postType && filterValues.postType === PostType;
            }

            return (filterAuthor === 0 || filterAuthor) &&
                    (filterType === 0 || filterType) ;
        });

        setFilteredPosts(somePosts);

    }, [filterValues, data])

    useEffect(() => {
        // Populate authors filter
        const authors = data.allStrapiEmployee?.edges?.map(({node}) =>  ({name: node.Name, id: node.createdById}));
        authors.unshift({
            name: 'All Authors',
            id: 0
        })
        setAuthorOptions(authors);

        // Populate post type filter
        const types = data.postTypes?.distinct?.map((type) =>  ({name: type, id: type, icon: <Icon type={type} />}));
        types.unshift({
            name: 'All Post Types',
            id: 0
        })
        setPostOptions(types);
    }, [data])

    return (
        <div className="bg-plum-100 py-10">
            <div className="max-full xl:mx-auto xl:max-w-7xl">
                <div className="max-full xl:mx-auto xl:max-w-7xl relative">
                    <div
                        className="flex mt-14 lg:mt-0 overflow-hidden w-full"
                        >
                    <div className="flex flex-nowrap mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4 w-full">
                            {isClient ? <div className="flex flex-inline col-span-1 md:col-span-2 lg:col-span-3">
                            <div className="md:flex md:flex-inline">
                            <div className="ml-2 md:ml-0 md:mt-[8px] m-2">
                                <SearchButton setOpen={setSearchOpen} size='large' colour='blue'/>
                            </div>
                            <DropDown 
                                value={filterValues.author} 
                                options={authorOptions} 
                                onSelect={(option) => {
                                    typeof window !== "undefined" && window?.gtag && window.gtag("event", "blog_filter", { 
                                        'event_category': 'author',
                                        'value': option.name
                                    })
                                    filterChange('author', option.id)}
                                }
                                icon={<UserIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />}
                                srLabel={'Select blog post author'}
                            />
                            <DropDown 
                                value={filterValues.postType} 
                                options={postOptions} 
                                onSelect={(option) => {
                                    typeof window !== "undefined" && window?.gtag && window.gtag("event", "blog_filter", { 
                                        'event_category': 'post_type',
                                        'value': option.name
                                    })
                                    filterChange('postType', option.id)}
                                } 
                                icon={<Icon className="-ml-0.5 h-5 w-5" aria-hidden="true" type={filterValues.postType} />}
                                srLabel={'Select blog post type'}
                            />
                            </div></div> : null}
                            {filteredPosts.slice(0, (perPage * page) + perPage).map((blog) => {
                                let author = "";
                                if (blog.node.Authors?.length > 0) {
                                    blog.node.Authors.forEach((a, idx) => {
                                        if (idx === 0) {
                                            author = a.Name
                                        } else {
                                            author += `, ${a.Name}`
                                        }
                                    })
                                } else {
                                    author = blog.node.createdBy?.firstname + ' ' + blog.node.createdBy?.lastname;
                                }
                                if (blog.node.Imported && !blog.node.FeatureImage) {
                                    return (
                                        <div className="" key={blog.node.id}>
                                            <BlogTile 
                                                type={blog.node.PostType}
                                                image={blog.node.FeatureImageUrl}
                                                title={blog.node.Title}
                                                date={blog.node.publishedAt}
                                                excerpt={blog.node.Excerpt || blog.node.Content[0]?.Body?.data?.Body || ''}
                                                author={author}
                                                link={'/community/blog/' + blog.node.Slug}
                                            />  
                                        </div>
                                        
                                    )
                                }
                                return (
                                    <div className="" key={blog.node.id}>
                                        <BlogTile 
                                            type={blog.node.PostType}
                                            image={blog.node.FeatureImage?.formats?.medium?.url || blog.node.FeatureImage?.url}
                                            title={blog.node.Title}
                                            date={blog.node.publishedAt}
                                            excerpt={blog.node.Excerpt || blog.node.Content[0]?.Body?.data?.Body || ''}
                                            author={author}
                                            link={'/community/blog/' + blog.node.Slug}
                                        />  
                                    </div>
                                )
                            })}
                        </div>
                    </div></div>
                </div>
            </div>
            <div ref={baseRef}></div>
            <SearchModal open={searchOpen} close={() => setSearchOpen(false)} />
        </div>
    )
}

export default BlogBlock;